/* Analytics · routes, drivers and incidents (2026-10-28), in the console's own
   design. The classic page's data and rules (data/analytics.ts); its Fuel tab
   is the Fuel page, and its Zana Mode briefing is on Overview. Incidents are
   the drivers' reports: the number beside Analytics in the menu is how many
   are still open, and closing one here also clears its alert. */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { activeTermStartYmd } from '../../utils/termDates';
import { windowBounds, windowStats } from '../../utils/routeWindow';
import { useFleet } from '../data/FleetData';
import type { IncidentReport } from '../data/model';
import {
  DriverRating, flagWords, isCredibleRoute, isStaleRoute, kmText, LEARNED_AFTER_TRIPS, minutesText, openIncidents, resolveIncidents,
  RouteProfile, routeHealth, routeName, routeSide, SIDES, sideLabel, useDriverRatings, usePinnedRuns, useRouteData, useRouteTrail,
  useWindowRuns, WindowKey,
} from '../data/analytics';
import { RouteMap } from '../RouteMap';
import { usePage, useShell } from '../Shell';
import { ConsoleModal, ModalButtons } from '../Modal';
import { Empty, PAGES, PageBody, PageSkeleton, Spark, StaleBanner, busWithPlate, dayMonth, initials, n0, plural, useAction, useGo } from '../kit';

type Tab = 'routes' | 'drivers' | 'incidents';
const TONE_CHIP = { ok: 'c-ok', mute: 'c-mute', warn: 'c-warn', bad: 'c-bad' } as const;

const Select: React.FC<{ value: string; onChange: (v: string) => void; label: string; children: React.ReactNode }> = ({ value, onChange, label, children }) => (
  <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className="tin" style={{ height: 38 }}>
    {children}
  </select>
);

const Stat: React.FC<{ k: string; big: React.ReactNode; unit?: string; note?: React.ReactNode }> = ({ k, big, unit, note }) => (
  <div className="stat">
    <span className="k">{k}</span>
    <span className="big" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{unit && <span className="unit">{unit}</span>}{big}</span>
    {note != null && <span className="sm" style={{ color: 'var(--faint)' }}>{note}</span>}
  </div>
);

/* ─────────────────────────────── Routes ─────────────────────────────── */

const RoutesTab: React.FC = () => {
  const f = useFleet();
  const go = useGo();
  const { dark, ask } = useShell();
  const { profiles, roads } = useRouteData(f.org.code);
  const termYmd = activeTermStartYmd(typeof f.org.raw.currentTermStartDate === 'string' ? f.org.raw.currentTermStartDate : null);
  const [win, setWin] = useState<WindowKey>('all');
  const [bus, setBus] = useState('all');
  const [selId, setSelId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('route'));
  const { runs, loading: runsLoading } = useWindowRuns(f, win, f.org.termStart);
  const pinned = usePinnedRuns(f.org.code, f.buses.map((b) => b.id));
  const navigate = useNavigate();
  const busName = (id: string) => f.buses.find((b) => b.id === id)?.name || id;
  const busPlate = (id: string) => f.buses.find((b) => b.id === id)?.plate || id;
  const driverName = (id: string) => f.drivers.find((d) => d.id === id)?.name || 'A driver';

  const credible = useMemo(() => (profiles || []).filter(isCredibleRoute), [profiles]);
  const learned = useMemo(() => credible.filter((p) => p.isLearned), [credible]);
  const learning = credible.filter((p) => !p.isLearned && p.tripCount >= 1);
  const shown = useMemo(() => learned.filter((p) => bus === 'all' || p.busId === bus)
    .sort((a, b) => a.effectivenessScore - b.effectivenessScore), [learned, bus]);
  const sel = credible.find((p) => p.id === selId) || null;
  const trail = useRouteTrail(sel);

  const bounds = useMemo(() => windowBounds(win === 'all' ? 'all' : win, { termStartYmd: termYmd, termEndYmd: typeof f.org.raw.currentTermEndDate === 'string' ? f.org.raw.currentTermEndDate : null }), [win, termYmd, f.org.raw.currentTermEndDate]);
  const windowed = win !== 'all';
  const statsFor = useMemo(() => {
    const m = new Map<string, ReturnType<typeof windowStats>>();
    if (!windowed) return m;
    const ok = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
    credible.forEach((p) => {
      const anchored = ok(p.startLat) && ok(p.startLng) && ok(p.endLat) && ok(p.endLng);
      m.set(p.id, windowStats(runs, {
        busId: p.busId, side: routeSide(p),
        start: anchored ? { lat: p.startLat as number, lng: p.startLng as number } : null,
        end: anchored ? { lat: p.endLat as number, lng: p.endLng as number } : null,
      }, bounds));
    });
    return m;
  }, [windowed, credible, runs, bounds]);

  const avgScore = shown.length ? Math.round(shown.reduce((a, p) => a + p.effectivenessScore, 0) / shown.length) : null;
  const needLook = shown.filter((p) => p.effectivenessScore < 60 || (p.flags || []).length > 0);
  const avgMin = shown.length ? Math.round(shown.reduce((a, p) => a + (p.avgDurationMinutes || 0), 0) / shown.length) : null;
  const withKm = shown.filter((p) => (p.avgDistanceKm ?? 0) > 0);
  const avgKm = withKm.length ? withKm.reduce((a, p) => a + (p.avgDistanceKm || 0), 0) / withKm.length : null;
  const runsInWin = shown.reduce((a, p) => a + (statsFor.get(p.id)?.runs ?? 0), 0);
  const roadsShown = roads.filter((r) => bus === 'all' || r.busId === bus);

  if (profiles === null) return <PageSkeleton stats rows={6} />;

  const time = (p: RouteProfile) => {
    if (!windowed) return minutesText(p.avgDurationMinutes);
    const st = statsFor.get(p.id);
    return st && st.avgMinutes != null ? minutesText(st.avgMinutes) : '—';
  };
  const dist = (p: RouteProfile) => {
    if (!windowed) return kmText(p.avgDistanceKm);
    const st = statsFor.get(p.id);
    return st && st.avgKm != null ? kmText(st.avgKm) : '—';
  };
  const count = (p: RouteProfile) => (windowed ? (statsFor.get(p.id)?.runs ?? 0) : p.tripCount);

  return (
    <>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <div className="seg" role="group" aria-label="Period">
          {(['all', 'd30', ...(termYmd ? ['term'] : [])] as WindowKey[]).map((k) => (
            <button key={k} type="button" className={win === k ? 'on' : ''} aria-pressed={win === k} onClick={() => setWin(k)}>
              {k === 'all' ? 'All time' : k === 'd30' ? 'Last 30 days' : 'This term'}
            </button>
          ))}
        </div>
        <Select label="Bus" value={bus} onChange={setBus}>
          <option value="all">All buses</option>
          {f.buses.map((b) => <option key={b.id} value={b.id}>{busWithPlate(b)}</option>)}
        </Select>
        <span className="sm" style={{ color: 'var(--faint)' }}>
          {windowed ? `Trips, times and distances ${bounds.label}${runsLoading ? ' — counting…' : ''}. The score is always all-time.` : 'Scores update after every finished trip.'}
        </span>
      </div>

      <div className="stats">
        <Stat k="Learned routes" big={shown.length} note={`on ${plural(new Set(shown.map((p) => p.busId)).size, 'bus', 'buses')}${learning.length ? ` · ${learning.length} still learning` : ''}`} />
        <Stat k="Average score" big={avgScore ?? '—'} note={avgScore != null ? `${routeHealth(avgScore).label} · out of 100` : 'needs a learned route'} />
        <Stat k="Need a look" big={needLook.length} note={needLook.length ? 'score under 60, or flagged' : 'none — all steady'} />
        {windowed
          ? <Stat k={`Trips ${bounds.label}`} big={runsLoading ? '…' : runsInWin} note="on learned routes" />
          : <Stat k="Average trip" big={avgMin != null ? <>{avgMin}<span className="unit" style={{ marginLeft: 6, marginRight: 0 }}>min</span></> : '—'} note={avgKm != null ? `${avgKm.toFixed(1)} km on average` : 'no distances yet'} />}
      </div>

      {needLook.length > 0 && (
        <div className="zcard" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, borderColor: 'var(--z-line)' }}>
          <span style={{ color: 'var(--z)' }}><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="5" /><circle cx="9.5" cy="12" r="1.6" fill="#fff" /><circle cx="14.5" cy="12" r="1.6" fill="#fff" /></svg></span>
          <span style={{ flex: 1 }}>{needLook.length === 1 ? `${routeName(needLook[0])} scores ${needLook[0].effectivenessScore}.` : `${needLook.length} routes score under 60 or carry a flag.`} Zana can tell you what is slowing {needLook.length === 1 ? 'it' : 'them'} down.</span>
          <button type="button" className="btn s b-line" onClick={() => ask(needLook.length === 1 ? `Why is the route ${routeName(needLook[0])} scoring ${needLook[0].effectivenessScore}?` : 'Which of my routes are slowing down, and why?')}>Ask why</button>
        </div>
      )}

      {learned.length === 0 ? (
        <section className="panel">
          <Empty title="No learned routes yet"
            body={`A route appears here after ${LEARNED_AFTER_TRIPS} matching trips — the same bus, the same morning or afternoon run. Every trip is already on its bus page.${learning.length ? ` ${plural(learning.length, 'route is', 'routes are')} still learning.` : ''}`} />
        </section>
      ) : (
        <div className="two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)', gap: 24, alignItems: 'stretch' }}>
          <section className="panel" style={{ overflow: 'hidden', minHeight: 420, display: 'flex' }}>
            <RouteMap routes={shown} selected={sel} trail={trail} onSelect={setSelId} dark={dark} />
          </section>
          <section className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
            {!sel ? (
              <Empty title="Pick a route" body="Tap a pin on the map or a row below. You’ll see its trip times, who drives it and why it scored what it did." />
            ) : (() => {
              const h = routeHealth(sel.effectivenessScore);
              const st = statsFor.get(sel.id);
              const drivers = Object.entries(sel.driverDurations || {}).map(([id, arr]) => {
                const xs = (arr || []).filter((n) => Number.isFinite(n));
                return { id, trips: xs.length, avg: xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : 0 };
              }).filter((d) => d.trips > 0).sort((a, b) => a.avg - b.avg);
              return (
                <>
                  <div className="ph" style={{ alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <span className="mono">{sideLabel(routeSide(sel))} · {busName(sel.busId)}</span>
                      <h2 className="h2" style={{ marginTop: 4 }}>{routeName(sel)}</h2>
                    </div>
                    <button type="button" className="ibtn" aria-label="Clear" onClick={() => setSelId(null)} style={{ width: 32, height: 32 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
                    <div className="row" style={{ gap: 10 }}>
                      <span className={`chip ${TONE_CHIP[h.tone]}`}>{sel.isLearned ? `${h.label} · ${sel.effectivenessScore}` : `Learning · ${sel.tripCount} of ${LEARNED_AFTER_TRIPS}`}</span>
                      {isStaleRoute(sel) && <span className="chip c-mute">No trips in 30 days</span>}
                    </div>
                    <p style={{ margin: 0, color: 'var(--ink2)' }}>{h.hint}</p>
                    <div className="kv">
                      <div><span>Usual time</span><b>{minutesText(sel.avgDurationMinutes)}</b></div>
                      <div><span>Distance</span><b>{kmText(sel.avgDistanceKm)}</b></div>
                      <div><span>Trips learned from</span><b className="num">{sel.tripCount}</b></div>
                      <div><span>{windowed ? `Trips ${bounds.label}` : 'Bus'}</span><b>{windowed ? (st ? st.runs : 0) : busPlate(sel.busId)}</b></div>
                    </div>
                    {(sel.flagReasons || []).length > 0 && (
                      <div>
                        <b style={{ fontSize: 14 }}>Why it lost points</b>
                        <ul className="zfacts" style={{ marginTop: 8 }}>{(sel.flagReasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </div>
                    )}
                    {(sel.recentDurations || []).length >= 2 && (
                      <div className="row" style={{ gap: 12 }}>
                        <span className="sm" style={{ color: 'var(--muted)', fontWeight: 600 }}>Recent trip times</span>
                        <Spark values={sel.recentDurations || []} tone={h.tone === 'bad' || h.tone === 'warn' ? 'bad' : 'ok'} />
                        <span className="sm num">latest {Math.round((sel.recentDurations || [])[(sel.recentDurations || []).length - 1])} min</span>
                      </div>
                    )}
                    {drivers.length > 0 && (
                      <div>
                        <b style={{ fontSize: 14 }}>Who drives it</b>
                        {drivers.map((d) => (
                          <div key={d.id} className="row" style={{ gap: 10, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
                            <span className="av" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(driverName(d.id))}</span>
                            <span style={{ flex: 1 }}>{driverName(d.id)}</span>
                            <span className="sm num" style={{ color: 'var(--muted)' }}>{d.avg} min · {plural(d.trips, 'trip')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: 'auto' }}>
                      <a className="btn s b-line" {...go('ConsoleBuses', `?bus=${encodeURIComponent(sel.busId)}`)}>Open {busName(sel.busId)}</a>
                    </div>
                  </div>
                </>
              );
            })()}
          </section>
        </div>
      )}

      {shown.length > 0 && (
        <section className="panel" style={{ overflow: 'hidden' }}>
          <div className="ph">
            <h2 className="h2">Routes</h2>
            <span className="sm" style={{ color: 'var(--muted)' }}>Lowest score first</span>
          </div>
          <div className="tblbox">
            <table className="t">
              <thead>
                <tr>
                  <th>Route</th><th>Usual time</th><th>Distance</th><th className="r">{windowed ? 'Trips' : 'Learned from'}</th><th>Score</th><th>Needs a look</th>
                </tr>
              </thead>
              {SIDES.map((side) => {
                const list = shown.filter((p) => routeSide(p) === side);
                if (!list.length) return null;
                return (
                  <tbody key={side}>
                    <tr style={{ cursor: 'default', background: 'var(--s2)' }}>
                      <td colSpan={6} className="mono" style={{ height: 34 }}>{sideLabel(side)} · {list.length}</td>
                    </tr>
                    {list.map((p) => {
                      const h = routeHealth(p.effectivenessScore);
                      const quiet = (windowed && count(p) === 0) || isStaleRoute(p);
                      return (
                        <tr key={p.id} className={selId === p.id ? 'sel' : ''} onClick={() => setSelId(p.id)} style={quiet ? { opacity: 0.6 } : undefined}>
                          <td>
                            <div className="who" style={{ maxWidth: 360 }}>
                              <b style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{routeName(p)}</b>
                              <span>{busPlate(p.busId)}{isStaleRoute(p) ? ' · no trips in 30 days' : ''}</span>
                            </div>
                          </td>
                          <td className="num">{time(p)}</td>
                          <td className="num">{dist(p)}</td>
                          <td className="r num">{count(p)}</td>
                          <td><span className={`chip ${TONE_CHIP[h.tone]}`}>{p.effectivenessScore} · {h.label}</span></td>
                          <td style={{ whiteSpace: 'normal', color: 'var(--ink2)', fontSize: 14 }}>{(p.flags || []).length ? flagWords(p.flags).join(' · ') : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                );
              })}
            </table>
          </div>
          {learning.length > 0 && (
            <p className="sm" style={{ margin: 0, padding: '12px 20px', borderTop: '1px solid var(--line)', color: 'var(--muted)' }}>
              {plural(learning.length, 'more route is', 'more routes are')} still learning — a route shows here after {LEARNED_AFTER_TRIPS} matching trips.
            </p>
          )}
        </section>
      )}

      {pinned.length > 0 && (
        <section className="panel" style={{ overflow: 'hidden' }}>
          <div className="ph"><h2 className="h2">Pinned trips</h2><span className="sm" style={{ color: 'var(--muted)' }}>Pinned from a bus’s runs · open one to see it on the map</span></div>
          <div className="tblbox">
            <table className="t">
              <thead><tr><th>Trip</th><th>Date</th><th>Distance</th><th className="r">Time</th></tr></thead>
              <tbody>
                {pinned.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`${PAGES.ConsoleBuses}?bus=${encodeURIComponent(r.busId)}&trip=${encodeURIComponent(r.id)}`)}>
                    <td><div className="who"><b>{r.name}</b><span>{busPlate(r.busId)}{r.type ? ` · ${r.type.replace(/_/g, ' ')}` : ''}</span></div></td>
                    <td>{r.date ? dayMonth(r.date) : '—'}</td>
                    <td className="num">{kmText(r.km)}</td>
                    <td className="r num">{minutesText(r.minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {roadsShown.length > 0 && (
        <section className="panel">
          <div className="ph"><h2 className="h2">Roads driven</h2><span className="sm" style={{ color: 'var(--muted)' }}>Which roads each bus takes, and which cost time</span></div>
          {roadsShown.sort((a, b) => (a.busId + a.side).localeCompare(b.busId + b.side)).map((r) => (
            <div key={r.id} className="todo" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <b>{busName(r.busId)} · {r.side === 'evening' ? 'afternoon' : r.side}</b>
                <span className="sm" style={{ color: 'var(--muted)' }}>{plural(r.roads.length, 'road')} · {plural(r.tripsConsidered, 'trip')}{r.evidence === 'thin' ? ' · early days' : ''}</span>
              </div>
              {r.sentence && <span style={{ color: 'var(--ink2)' }}>{r.sentence}</span>}
              {r.roads.length > 1 && r.roads.map((rd, i) => (
                <div key={rd.roadId} className="row sm" style={{ gap: 10 }}>
                  <span className={`chip ${rd.roadId === r.fastestRoadId ? 'c-ok' : rd.roadId === r.slowestRoadId ? 'c-warn' : 'c-mute'}`} style={{ minWidth: 72, justifyContent: 'center' }}>
                    {rd.roadId === r.fastestRoadId ? 'Fastest' : rd.roadId === r.slowestRoadId ? 'Slowest' : `Road ${i + 1}`}
                  </span>
                  <b>{rd.name || `Road ${i + 1}`}</b>
                  <span style={{ color: 'var(--muted)' }}>
                    {rd.avgMinutes} min · {plural(rd.avgStops, 'stop')} · {plural(rd.trips, 'trip')}{rd.isLearned ? '' : ' · still learning'}
                    {rd.crawlZones[0] ? ` · slow for ~${Math.round(rd.crawlZones[0].avgSeconds / 60)} min${rd.crawlZones[0].hourLocal != null ? ` around ${rd.crawlZones[0].hourLocal}:00` : ''}` : ''}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      <details className="sm" style={{ color: 'var(--muted)' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>How the score works</summary>
        <p style={{ margin: '8px 0 0', maxWidth: 760, lineHeight: '20px' }}>
          Every route starts at 100. It loses points when something goes wrong on a trip (an incident), when trips suddenly take longer than usual,
          when they get slower week after week, or when the time jumps around from day to day. With no problems it stays at 100.
          A route shows here after {LEARNED_AFTER_TRIPS} matching trips and is confirmed after 8. Trips shorter than half a kilometre are left out.
        </p>
      </details>
    </>
  );
};

/* ─────────────────────────────── Drivers ─────────────────────────────── */

const Bar: React.FC<{ v: number; label: string }> = ({ v, label }) => {
  const x = Math.max(0, Math.min(100, Math.round(v || 0)));
  const color = x >= 70 ? 'var(--ink)' : x >= 45 ? 'var(--warn)' : 'var(--bad)';
  return (
    <div className="row" style={{ gap: 8, minWidth: 104 }} role="img" aria-label={`${label} ${x} of 100`}>
      <span style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--s2)', position: 'relative', overflow: 'hidden' }}>
        <span style={{ position: 'absolute', inset: 0, width: `${x}%`, background: color, borderRadius: 3 }} />
      </span>
      <b className="num" style={{ width: 26, textAlign: 'right', fontSize: 14 }}>{x}</b>
    </div>
  );
};

const Stars: React.FC<{ v: number }> = ({ v }) => {
  const full = Math.round(v || 0);
  return (
    <span aria-label={`${(v || 0).toFixed(1)} of 5 stars`} style={{ letterSpacing: 1, whiteSpace: 'nowrap' }}>
      <span style={{ color: 'var(--ink)' }}>{'★'.repeat(Math.max(0, full))}</span>
      <span style={{ color: 'var(--line2)' }}>{'★'.repeat(Math.max(0, 5 - full))}</span>
      <span className="num" style={{ marginLeft: 8, fontWeight: 600 }}>{(v || 0).toFixed(1)}</span>
    </span>
  );
};

const DriversTab: React.FC = () => {
  const f = useFleet();
  const navigate = useNavigate();
  const { ask } = useShell();
  const ratings = useDriverRatings(f.org.code);
  const name = (id: string) => f.drivers.find((d) => d.id === id)?.name || f.parents.find((d) => d.id === id)?.name || 'Driver';
  const ranked = useMemo(() => [...(ratings || [])].sort((a, b) => (b.stars ?? 0) - (a.stars ?? 0)), [ratings]);
  // Lowest with trips behind it — the driver Zana names (engine/takeover.js lowestRatedDriver).
  const qualified = ranked.filter((d) => Number(d.stars) > 0 && Number(d.windowTrips || d.totalTrips || 0) > 0);
  const lowest: DriverRating | null = qualified.length ? qualified[qualified.length - 1] : null;
  const avg = qualified.length ? qualified.reduce((a, d) => a + d.stars, 0) / qualified.length : null;
  /* Flags = the driver's incident reports still open. The rating's own
     activeIncidentFlags only ever counts up: the driver's phone adds one per
     report and nothing the office can write takes it off (the rules let only
     that phone write the rating), so a closed incident stayed flagged. */
  const openFlags = useMemo(() => {
    const m = new Map<string, number>();
    openIncidents(f).forEach((r) => { if (r.driverId) m.set(r.driverId, (m.get(r.driverId) || 0) + 1); });
    return m;
  }, [f]);

  if (ratings === null) return <PageSkeleton stats rows={6} />;
  if (!ranked.length) {
    return (
      <section className="panel">
        <Empty title="No driver ratings yet" body="Each driver is rated as they finish trips — safety, fuel, keeping tracking on, and time against the route’s usual." />
      </section>
    );
  }
  return (
    <>
      <div className="stats">
        <Stat k="Drivers rated" big={ranked.length} note={`of ${plural(f.drivers.length, 'driver')}`} />
        <Stat k="Average" big={avg != null ? avg.toFixed(1) : '—'} note="stars out of 5" />
        <Stat k="Top driver" big={<span style={{ fontSize: 26 }}>{name(ranked[0].driverId)}</span>} note={`${(ranked[0].stars ?? 0).toFixed(1)} stars${ranked[0].totalTrips != null ? ` · ${plural(ranked[0].totalTrips, 'trip')}` : ''}`} />
        <Stat k="Needs a word" big={<span style={{ fontSize: 26 }}>{lowest ? name(lowest.driverId) : '—'}</span>} note={lowest ? `${lowest.stars.toFixed(1)} stars` : 'nobody yet'} />
      </div>
      <section className="panel" style={{ overflow: 'hidden' }}>
        <div className="ph">
          <h2 className="h2">Ranking</h2>
          {lowest && <button type="button" className="btn s b-line" onClick={() => ask(`Why is ${name(lowest.driverId)} rated ${lowest.stars.toFixed(1)} stars, and what should I tell them?`)}>Ask Zana about {name(lowest.driverId).split(' ')[0]}</button>}
        </div>
        <div className="tblbox">
          <table className="t">
            <thead>
              <tr>
                <th style={{ width: 56 }}>Rank</th><th>Driver</th><th>Stars</th>
                <th title="Incidents and speeding over the last 30 trips">Safety</th><th title="Refills flagged as not adding up, last 8">Fuel</th>
                <th title="Share of the last 30 trips that were tracked">Tracking</th><th title="Share of the last 30 trips within 15% of the route’s usual time">On time</th>
                <th className="r">Trips</th><th title="Incident reports still open">Flags</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((d, i) => (
                <tr key={d.id} onClick={() => navigate(`${PAGES.ConsolePeople}?person=${encodeURIComponent(d.driverId)}`)}>
                  <td className="num" style={{ fontWeight: 700, color: i < 3 ? 'var(--ink)' : 'var(--muted)' }}>#{i + 1}</td>
                  <td>
                    <div className="row" style={{ gap: 10 }}>
                      <span className="av" style={{ width: 30, height: 30, fontSize: 12 }}>{initials(name(d.driverId))}</span>
                      <b>{name(d.driverId)}</b>
                      {(d.windowTrips ?? 0) > 0 && (d.windowTrips ?? 0) < 10 && <span className="chip c-mute" title="Fewer than 10 recent trips — the score is still settling">New</span>}
                    </div>
                  </td>
                  <td><Stars v={d.stars ?? 0} /></td>
                  <td><Bar v={d.safetyScore} label="Safety" /></td>
                  <td><Bar v={d.fuelScore} label="Fuel" /></td>
                  <td><Bar v={d.coverageScore ?? d.complianceScore} label="Tracking" /></td>
                  <td><Bar v={d.timeScore} label="On time" /></td>
                  <td className="r num">{d.totalTrips ?? '—'}</td>
                  <td>{(openFlags.get(d.driverId) || 0) > 0 ? <span className="chip c-warn">{plural(openFlags.get(d.driverId) || 0, 'open incident')}</span> : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="sm" style={{ margin: 0, color: 'var(--muted)', maxWidth: 760 }}>
        Each score is out of 100, worked out on the driver’s phone at the end of every trip from their last 30 trips: on time within 15% of the route’s usual time,
        tracked with GPS on, safety from incidents and speeding, fuel from refills that didn’t add up. Stars weigh safety most, then on time, tracking and fuel.
        A driver marked New has fewer than 10 recent trips, so their score is still settling.
      </p>
    </>
  );
};

/* ─────────────────────────────── Incidents ─────────────────────────────── */

const sevOf = (s: string | null) => {
  const x = (s || '').toLowerCase();
  return x === 'critical' || x === 'high' ? 'high' : x === 'medium' ? 'medium' : 'low';
};
const SEV_CHIP = { high: ['c-bad', 'High'], medium: ['c-warn', 'Medium'], low: ['c-mute', 'Low'] } as const;

const IncidentsTab: React.FC = () => {
  const f = useFleet();
  const go = useGo();
  const action = useAction();
  const { currentUser } = useAuth();
  const [view, setView] = useState<'open' | 'all'>('open');
  const [days, setDays] = useState('90');
  const [bus, setBus] = useState('all');
  const [closing, setClosing] = useState<IncidentReport[] | null>(null);
  const busLabel = (id: string) => {
    const b = f.buses.find((x) => x.id === id);
    return b ? busWithPlate(b) : id;
  };
  const driverName = (id: string | null) => (id ? f.drivers.find((d) => d.id === id)?.name || null : null);

  if (f.incidents === null) return <PageSkeleton stats rows={5} />;
  const open = openIncidents(f);
  const cutoff = days === 'all' ? 0 : Date.now() - Number(days) * 86400000;
  const list = (view === 'open' ? open : f.incidents)
    .filter((r) => (bus === 'all' || r.busId === bus) && (view === 'open' || !r.at || r.at.getTime() >= cutoff));
  const high = list.filter((r) => sevOf(r.severity) === 'high').length;
  const byBus = new Map<string, number>();
  list.forEach((r) => byBus.set(r.busId, (byBus.get(r.busId) || 0) + 1));
  const most = [...byBus.entries()].sort((a, b) => b[1] - a[1])[0];

  const close = (rs: IncidentReport[]) => {
    setClosing(null);
    void action({
      busy: rs.length === 1 ? 'Closing the incident…' : `Closing ${rs.length} incidents…`,
      done: rs.length === 1 ? 'Incident closed. It stays on the bus’s history.' : `${rs.length} incidents closed. They stay on each bus’s history.`,
      fail: 'Couldn’t close that. Try again in a minute.',
      run: () => resolveIncidents(f, rs, currentUser?.uid ?? null),
    });
  };

  return (
    <>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <div className="seg" role="group" aria-label="Which incidents">
          <button type="button" className={view === 'open' ? 'on' : ''} aria-pressed={view === 'open'} onClick={() => setView('open')}>Open {open.length}</button>
          <button type="button" className={view === 'all' ? 'on' : ''} aria-pressed={view === 'all'} onClick={() => setView('all')}>All</button>
        </div>
        {view === 'all' && (
          <Select label="Period" value={days} onChange={setDays}>
            <option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="all">All time</option>
          </Select>
        )}
        <Select label="Bus" value={bus} onChange={setBus}>
          <option value="all">All buses</option>
          {f.buses.map((b) => <option key={b.id} value={b.id}>{busWithPlate(b)}</option>)}
        </Select>
      </div>

      <div className="stats">
        <Stat k={view === 'open' ? 'Open' : 'Reported'} big={list.length} note={view === 'open' ? 'waiting to be closed' : days === 'all' ? 'all time' : `last ${days} days`} />
        <Stat k="High severity" big={high} note={list.length ? `${Math.round((high / list.length) * 100)}% of these` : '—'} />
        <Stat k="Most on one bus" big={<span style={{ fontSize: 26 }}>{most ? busLabel(most[0]).split(' · ')[0] : '—'}</span>} note={most ? plural(most[1], 'incident') : 'none'} />
        <Stat k="Last reported" big={<span style={{ fontSize: 26 }}>{list[0]?.at ? dayMonth(list[0].at) : '—'}</span>} note={list[0]?.issueType || '—'} />
      </div>

      <section className="panel" style={{ overflow: 'hidden' }}>
        <div className="ph">
          <h2 className="h2">{view === 'open' ? 'Open incidents' : 'Incidents'}</h2>
          {view === 'open' && list.length > 1 && <button type="button" className="btn s b-line" onClick={() => setClosing(list)}>Close all {list.length}</button>}
        </div>
        {list.length === 0 ? (
          <Empty
            icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-10" /></svg>}
            title={view === 'open' ? 'No open incidents' : 'No incidents in this period'}
            body="When a driver reports a breakdown or a problem in the Trasse app, it appears here and the number beside Analytics goes up until you close it."
          />
        ) : list.map((r) => {
          const [cls, lbl] = SEV_CHIP[sevOf(r.severity)];
          const who = driverName(r.driverId);
          return (
            <div key={r.id} className="todo" style={r.resolved ? { opacity: 0.7 } : undefined}>
              <span className={`chip ${cls}`} style={{ width: 84, justifyContent: 'center', flex: 'none' }}>{lbl}</span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <b>{r.issueType}</b>
                <span className="sm" style={{ color: 'var(--muted)', fontSize: 14 }}>
                  {[busLabel(r.busId), who ? `reported by ${who}` : '', r.details].filter(Boolean).join(' · ')}
                </span>
              </div>
              <span className="mono desk" style={{ width: 92 }}>{r.at ? dayMonth(r.at) : '—'}</span>
              <a className="btn s b-line" {...go('ConsoleBuses', `?bus=${encodeURIComponent(r.busId)}`)}>See bus</a>
              {r.resolved
                ? <span className="chip c-ok" style={{ flex: 'none' }}>Closed{r.resolvedAt ? ` ${dayMonth(r.resolvedAt)}` : ''}</span>
                : <button type="button" className="btn s b-ink" onClick={() => close([r])}>Close</button>}
            </div>
          );
        })}
      </section>
      {closing && (
        <ConsoleModal title={`Close ${plural(closing.length, 'incident')}?`} onClose={() => setClosing(null)} width={520}
          footer={<ModalButtons cancel="Keep them open" onCancel={() => setClosing(null)} primary={`Close ${closing.length}`} onPrimary={() => close(closing)} />}>
          <p style={{ margin: 0, fontSize: 16, lineHeight: '24px' }}>They leave this list and the count beside Analytics. Each report stays on its bus’s history.</p>
        </ConsoleModal>
      )}
    </>
  );
};

/* ─────────────────────────────── Page ─────────────────────────────── */

const Analytics: React.FC = () => {
  const f = useFleet();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = raw === 'drivers' || raw === 'incidents' ? raw : 'routes';
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === 'routes') next.delete('tab'); else next.set('tab', t);
    next.delete('route');
    setParams(next, { replace: true });
  };
  const open = openIncidents(f).length;

  usePage({
    key: 'ConsoleAnalytics', title: 'Analytics',
    zanaContext: `Analytics · ${tab === 'routes' ? 'Routes' : tab === 'drivers' ? 'Drivers' : 'Incidents'}`,
    zanaIntro: 'I can explain a route’s score, a driver’s rating, or what an incident means for a bus.',
    prompts: tab === 'drivers'
      ? ['Who are my best drivers?', 'Which driver needs a word, and why?', 'How is a driver’s score worked out?']
      : tab === 'incidents'
        ? ['Which bus breaks down the most?', 'What did drivers report this month?', 'Are incidents going up?']
        : ['Which route is slowest this term?', 'Why did a route lose points?', 'Which roads cost the most time?'],
  });

  return (
    <PageBody loading={f.loading} skeleton={<PageSkeleton stats rows={6} />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: 1240 }}>
        {f.error && <StaleBanner what="analytics" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 className="h1">Analytics</h1>
            <p className="sub">Routes, drivers and incidents across {plural(f.buses.length, 'bus', 'buses')}. {n0(f.runs.filter((r) => r.status === 'completed').length)} trips in the last 30 days.</p>
          </div>
          <div className="seg" role="tablist" aria-label="Analytics">
            {(['routes', 'drivers', 'incidents'] as Tab[]).map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
                {t === 'routes' ? 'Routes' : t === 'drivers' ? 'Drivers' : 'Incidents'}
                {t === 'incidents' && open > 0 && <span className="cnt bad" style={{ marginLeft: 2 }}>{open}</span>}
              </button>
            ))}
          </div>
        </div>
        {tab === 'routes' && <RoutesTab />}
        {tab === 'drivers' && <DriversTab />}
        {tab === 'incidents' && <IncidentsTab />}
      </div>
    </PageBody>
  );
};

export default Analytics;
